import { useSetState } from 'ahooks'
import { Upload, UploadChangeParam, UploadFile, UploadProps } from './upload'
import { getBase64 } from './utils'
import { ImageCrop, message, Modal } from 'components/common'
import { Typography } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
const { Text } = Typography
export interface ImageUploadProps
  extends Pick<
    UploadProps,
    | 'action'
    | 'defaultFileList'
    | 'disabled'
    | 'fileList'
    | 'headers'
    | 'method'
    | 'multiple'
    | 'name'
    | 'type'
    | 'onChange'
    | 'onRemove'
  > {
  // 選択可能なファイル拡張子のリスト
  accept?: string[]
  // 最大アップロード可能数
  max?: number
}

interface PreviewData {
  image: string
  visible: boolean
  title: string
}

const defaultAccept = ['.jpeg', '.jpg', '.png', '.gif', '.avif', '.webp']

const style = {
  backgroundColor: '#fafafa',
  color: '#1890ff',
}

export const ImageUpload = ({
  accept = defaultAccept,
  fileList,
  max,
  onChange,
  onRemove,
  ...restProps
}: ImageUploadProps): JSX.Element => {
  const [previewData, setPreviewData] = useSetState<PreviewData>({
    image: '',
    visible: false,
    title: '',
  })

  const isAcceptableFile = (file: UploadFile) => {
    return accept.some((value) => file.name.toLowerCase().endsWith(value))
  }

  const beforeUpload = (file: UploadFile) => {
    if (typeof max === 'number' && fileList.length >= max) {
      message.error('最大アップロード可能数を超えています。')
      return Upload.LIST_IGNORE
    }

    const acceptable = isAcceptableFile(file)
    if (!acceptable) {
      message.error('許可されていないファイル形式です。')
    }
    return acceptable ? true : Upload.LIST_IGNORE
  }

  const handleChange = ({ file, fileList, event }: UploadChangeParam) => {
    return onChange?.({ file, fileList, event })
  }

  const handleRemove = (
    file: UploadFile
  ): ReturnType<ImageUploadProps['onRemove']> => {
    return onRemove?.(file)
  }

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj)
    }

    setPreviewData({
      image: file.url || file.preview,
      visible: true,
      title: file.name,
    })
  }

  const handlePreviewCancel = () => {
    setPreviewData({ visible: false })
  }

  return (
    <>
      <ImageCrop aspect={16 / 9} modalTitle="画像を編集する">
        <Upload
          listType="picture-card"
          accept={accept.join(',')}
          beforeUpload={beforeUpload}
          fileList={fileList}
          onChange={handleChange}
          onRemove={handleRemove}
          onPreview={handlePreview}
          {...restProps}
        >
          {/* 画像が最大枚数アップロードされているときは画像追加のボタンを出さない */}
          {fileList.length < max && <div style={style}>画像を追加する</div>}
        </Upload>
      </ImageCrop>

      {/* 画像プレビューモーダル */}
      <Modal
        open={previewData.visible}
        title={previewData.title}
        footer={null}
        width={800}
        onCancel={handlePreviewCancel}
      >
        <img style={{ width: '100%' }} src={previewData.image} alt="preview" />
      </Modal>

      {/* 注意書き */}
      <div className=" bg-red-100 p-4 border-solid border-red-400 border-2 rounded-md">
        <ExclamationCircleOutlined />
        <Text strong>画像のアップロード・削除は即時自動保存されます。</Text>
      </div>
    </>
  )
}
